import React, { useEffect, useState } from "react";
import axios from "axios";
import { Users, AlertTriangle, Clock, CheckCircle2, List, BarChart3 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

const API = window.location.origin + "/api";

const COULEUR_CONFIG = {
  blanc: { label: "Sur liste d'attente", color: "#94a3b8", bg: "bg-slate-100", text: "text-slate-700", icon: List },
  bleu:  { label: "En cours d'orientation", color: "#3b82f6", bg: "bg-blue-50", text: "text-blue-700", icon: CheckCircle2 },
  rouge: { label: "A Supprimer", color: "#ef4444", bg: "bg-red-50", text: "text-red-700", icon: AlertTriangle },
  gris:  { label: "A Revoir", color: "#64748b", bg: "bg-slate-200", text: "text-slate-600", icon: Clock },
};

const PIE_COLORS = ["#94a3b8", "#3b82f6", "#ef4444", "#64748b"];

const RESIDENCE_SHORT = {
  "RESIDENCE SOCIALE": "Rés. Sociale",
  "RESIDENCE ACCUEIL BAUDELAIRE": "Baudelaire",
  "CHRS DIFFUS": "CHRS Diffus",
  "CHRS COLLECTIF FEMMES": "CHRS Femmes",
  "CHRS SOS VIOLENCES CONJUGALES": "SOS VC",
  "CHRS CO LOCATION ILOT JEUNES": "Îlot Jeunes",
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/stats`)
      .then(res => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!stats) return null;

  const pieData = Object.entries(stats.by_couleur).map(([couleur, count]) => ({
    name: COULEUR_CONFIG[couleur]?.label || couleur,
    value: count,
    couleur,
  }));

  const barData = stats.by_residence.map(item => ({
    name: RESIDENCE_SHORT[item.residence] || item.residence,
    count: item.count,
    full: item.residence,
  }));

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          Tableau de bord
        </h1>
        <p className="text-slate-500 text-sm mt-1">Vue d'ensemble de la liste d'attente</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
        {/* Total */}
        <div className="bg-indigo-600 rounded-xl p-5 text-white shadow-sm xl:col-span-1" data-testid="stat-total">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-200">Total</p>
            <Users className="w-5 h-5 text-indigo-200" />
          </div>
          <p className="text-4xl font-bold">{stats.total}</p>
          <p className="text-indigo-200 text-xs mt-1">demandes</p>
        </div>

        {/* Per color */}
        {Object.entries(COULEUR_CONFIG).map(([couleur, cfg]) => {
          const Icon = cfg.icon;
          return (
            <div key={couleur} className={`${cfg.bg} rounded-xl p-5 border border-slate-200 shadow-sm`}
              data-testid={`stat-${couleur}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {cfg.label.split(" ")[0]}
                </p>
                <Icon className={`w-5 h-5 ${cfg.text}`} />
              </div>
              <p className={`text-3xl font-bold ${cfg.text}`}>{stats.by_couleur[couleur] || 0}</p>
              <p className="text-slate-400 text-xs mt-1 truncate">{cfg.label}</p>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pie chart — 1 colonne */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="chart-statuts">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-indigo-600" />
            <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Répartition par statut
            </h2>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={75} outerRadius={110}
                dataKey="value" paddingAngle={3}>
                {pieData.map((entry, index) => (
                  <Cell key={entry.couleur} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [value, name]} />
              <Legend
                content={({ payload }) => (
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mt-3 px-2">
                    {payload.map((entry, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <div style={{ width: 10, height: 10, backgroundColor: entry.color, borderRadius: 2, flexShrink: 0 }} />
                        <span className="text-xs text-slate-600 truncate">{entry.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart — 2 colonnes */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="chart-residences">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-indigo-600" />
            <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Demandes par résidence
            </h2>
          </div>
          <ResponsiveContainer width="100%" height={Math.min(420, barData.length * 21)}>
            <BarChart data={barData} layout="vertical" margin={{ left: 0, right: 20, top: 0, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis
                type="category"
                dataKey="name"
                width={185}
                tick={{ fontSize: 9, fill: "#475569" }}
                tickFormatter={(v) => v.length > 28 ? v.slice(0, 26) + "…" : v}
              />
              <Tooltip
                formatter={(value, _, props) => [value, props.payload?.full]}
                contentStyle={{ fontSize: 11 }}
              />
              <Bar dataKey="count" fill="#4F46E5" radius={[0, 3, 3, 0]} maxBarSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
